import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import {
  faCube,
  faGears,
  faShoePrints,
  faUserGroup,
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { ISidebarSectionItem } from './SidebarSection';
import {
  IParsedOrganizationUserRoles,
  parseOrganizationUserRoles,
} from '~/helpers/parseOrganizationUserRoles';
import { useOrganizationUserQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationUserQuery';
import { SidebarSection } from './SidebarSection';

export type IOrganizationSidebarContentProps = IContainerProps & {
  organizationId: string;
};

type IGetItemsProps = {
  baseUrl: string;
  t: TFunction<'translation', undefined>;
  roles?: IParsedOrganizationUserRoles;
};

const getItems = ({
  baseUrl,
  roles,
  t,
}: IGetItemsProps): Array<ISidebarSectionItem> => [
  {
    label: t('common.onboarding'),
    faLeadingIcon: faShoePrints,
    href: `${baseUrl}/onboarding`,
  },
  {
    label: t('OrganizationSidebarContent.applications'),
    faLeadingIcon: faCube,
    href: `${baseUrl}/apps`,
    matchPatterns: [`${baseUrl}/apps`, `${baseUrl}/apps/*`],
  },
  {
    label: t('common.organizationUsers'),
    faLeadingIcon: faUserGroup,
    href: `${baseUrl}/users`,
    matchPatterns: [
      `${baseUrl}/users`,
      `${baseUrl}/users/*`,
      `${baseUrl}/invitations`,
      `${baseUrl}/invitations/*`,
    ],
  },
  ...(roles?.atLeastAdmin
    ? [
        {
          label: t('OrganizationSidebarContent.settings'),
          faLeadingIcon: faGears,
          href: `${baseUrl}/settings`,
        },
      ]
    : []),
];

export const OrganizationSidebarContent: React.FC<
  IOrganizationSidebarContentProps
> = (props) => {
  const { organizationId, ...other } = props;
  const { t } = useTranslation();
  const organizationUserQuery = useOrganizationUserQuery({ organizationId });
  const organizationUserRoles = organizationUserQuery.data?.roles
    ? parseOrganizationUserRoles(organizationUserQuery.data?.roles)
    : undefined;

  const baseUrl = `/organizations/${organizationId}`;
  const items = useMemo(
    () => getItems({ baseUrl, t, roles: organizationUserRoles }),
    [baseUrl, t, organizationUserRoles],
  );

  return <SidebarSection items={items} {...other} />;
};
